/**
 * Text-metric normalization for html2canvas exports.
 *
 * Three classes of ambiguity removed in the cloned DOM (live tree untouched):
 *
 *   1. Resolve `normal` keywords to browser-computed pixel values.
 *      `line-height: normal` resolves per-font from ascent+descent
 *      (≈1.15–1.5×); html2canvas uses a flat ~1.0× multiplier instead.
 *      Same drift exists for `letter-spacing` and `word-spacing`.
 *      Fix: read the live element's `getComputedStyle` and write the
 *      resolved value back as an inline px value on the clone.
 *
 *   2. Pin deterministic text-rendering hints. Browsers honour these for
 *      live rendering, html2canvas may not — but pinning them removes
 *      any keyword ambiguity (e.g. `auto`) and makes the value explicit.
 *      Applied to every element so anything text-bearing inherits.
 *      No layout effect when the keyword was already deterministic.
 *
 *   3. Resolve `transform: none` → explicit identity matrix. html2canvas's
 *      transform reader is keyword-sensitive; pinning to `matrix(...)`
 *      eliminates a code path where `none` is interpreted as missing.
 *
 * NOTE: this is metric resolution, not offset patching. The values come
 * from `getComputedStyle` on the live tree — the exact value the editor
 * renders at — so the export's text rasterizes from the same metrics.
 */

const NORMAL_PROPS = ['lineHeight', 'letterSpacing', 'wordSpacing'];
const CSS_PROP_NAME = {
  lineHeight: 'line-height',
  letterSpacing: 'letter-spacing',
  wordSpacing: 'word-spacing',
};

// Properties that browsers may resolve differently from html2canvas if left
// to their `normal`/`auto` defaults. Pinning to explicit values gives both
// renderers the same input. NOTE: deliberately NOT pinning `text-rendering`
// or `font-feature-settings` — those change SVG glyph positioning (the SVG
// text serializer's output path) in ways the editor's HTML rendering does
// not, causing extra letter-spacing and clipped first/last glyphs. Only
// pin properties that match the browser's default for HTML text rendering.
const PINNED_TEXT_HINTS = {
  '-webkit-font-smoothing': 'antialiased',
  '-moz-osx-font-smoothing': 'grayscale',
  'font-synthesis': 'none',
};

export function normalizeTextMetricsForExport(liveRoot, clonedRoot) {
  if (!liveRoot || !clonedRoot) return;

  const liveAll  = liveRoot.querySelectorAll('*');
  const cloneAll = clonedRoot.querySelectorAll('*');
  const len = Math.min(liveAll.length, cloneAll.length);
  if (len === 0) return;

  const diag = typeof window !== 'undefined' && window.__exportDiagnostics;
  let normalizedCount = 0;

  for (let i = 0; i < len; i++) {
    const live  = liveAll[i];
    const clone = cloneAll[i];
    if (!live || !clone || clone.nodeType !== 1 || !clone.style) continue;

    // (1) Resolve `normal` keywords from live computed style.
    let touched = false;
    for (const prop of NORMAL_PROPS) {
      const inline = clone.style[prop];
      if (inline && inline.trim().toLowerCase() === 'normal') {
        const cs = window.getComputedStyle(live);
        const resolved = cs[prop];
        if (resolved && resolved !== 'normal') {
          clone.style.setProperty(CSS_PROP_NAME[prop], resolved);
          touched = true;
        }
      }
    }

    // (2) Pin text-rendering hints on text-bearing elements. We treat any
    //     element with an inline font-family or font-size as text-bearing
    //     (covers every text element rendered by CanvasEditor).
    const isTextBearing = clone.style.fontFamily || clone.style.fontSize;
    if (isTextBearing) {
      for (const [prop, val] of Object.entries(PINNED_TEXT_HINTS)) {
        if (!clone.style.getPropertyValue(prop)) {
          clone.style.setProperty(prop, val);
        }
      }
      touched = true;
    }

    // (3) Resolve `transform: none` to identity for elements that may carry
    //     a transform attribute. Only touch elements that have an inline
    //     transform style (avoids polluting unrelated elements).
    const t = clone.style.transform;
    if (t && t.trim().toLowerCase() === 'none') {
      clone.style.transform = 'matrix(1, 0, 0, 1, 0, 0)';
      touched = true;
    }

    if (touched) normalizedCount++;
  }

  if (diag) {
    // eslint-disable-next-line no-console
    console.log('[export-norm] text/style normalized on', normalizedCount, 'nodes');
  }
}
